// Marketing de la tienda. Backend: MarketingController, MarketingUtility, AdsCampaignUtility.
// Crear descuentos (ofertas, promociones, cupones) no cuesta; publicitarlos (banner, flash, push) sí, según el plan.

// Contenido de la tienda: los tres descuentos y los eventos (sin descuento)
export type DiscountType = 'offers' | 'promotions' | 'coupons' | 'events';
export type MarketingStatus = 'active' | 'scheduled' | 'finished' | 'inactive';

// discount_types: 1 porcentaje, 2 valor fijo
export const DISCOUNT_PERCENT = 1;
export const DISCOUNT_FIXED = 2;

export interface DiscountItem {
    id: number;
    name: string;
    description: string | null;
    discount_types_id: number;      // no aplica a eventos
    discount_value: number;
    start_date: string;
    end_date: string;
    min_value: number | null;     // oferta: precio mínimo · cupón: compra mínima
    max_value: number | null;     // oferta: precio máximo · cupón: tope del descuento
    is_active: boolean;
    status: MarketingStatus;
    // Promociones
    products?: { id: number; name: string }[];
    // Cupones
    code?: string;
    usage_limit?: number;           // 0 = sin límite
    usage_limit_per_user?: number;
    uses_count?: number;
    // Eventos (start_date/end_date = cuándo ocurre)
    image_url?: string | null;
    location?: string | null;
    // Promociones de compra: nxm "lleva N, paga M" · gift "compra N y te regalamos Y"
    promotion_type?: PromotionType | null;
    buy_quantity?: number | null;
    pay_quantity?: number | null;
    gift_products_id?: number | null;
    gift_variants_id?: number | null;
    gift_quantity?: number | null;
    label?: string;
}

export type PromotionType = 'nxm' | 'gift';

export interface DiscountForm {
    name: string;
    description: string;
    discount_types_id: number;
    discount_value: number | '';
    start_date: string;   // datetime-local (YYYY-MM-DDTHH:mm)
    end_date: string;
    min_value: number | '';
    max_value: number | '';
    is_active: boolean;
    product_ids: number[];
    code: string;
    usage_limit: number | '';
    usage_limit_per_user: number | '';
    location: string;
    promotion_type: PromotionType;
    buy_quantity: number | '';
    pay_quantity: number | '';
    gift_products_id: number | '';
    gift_variants_id: number | '';
    gift_quantity: number | '';
}

// services (canales) y service_types (qué se anuncia) de la BD
export const SERVICE_PUSH = 3;
export const SERVICE_BANNER = 4;
export const SERVICE_FLASH = 5;

export const SERVICE_TYPE_PROMOTIONS = 2;
export const SERVICE_TYPE_EVENTS = 3;
export const SERVICE_TYPE_OFFERS = 4;
export const SERVICE_TYPE_COUPONS = 5;

// billing_types: 1 por usuario, 2 por impresión, 3 por click
export const BILLING_PER_USER = 1;
export const BILLING_IMPRESSION = 2;
export const BILLING_CLICK = 3;

export type AudienceSegment = 'municipality' | 'followers' | 'customers';

export interface AdPrice {
    service_service_types_id: number;
    service_id: number;
    service_type_id: number;
    billing_type_id: number;
    price: number;
}

export interface AdsPricing {
    plan_id: number | null;
    plan_name: string | null;
    balance: number;
    prices: AdPrice[];
}

export interface AdChannel {
    service_id: number;
    unit_price: number | null;
    click_price: number | null;
    recipients_count: number | null;
    sent_count: number | null;
    sent_at: string | null;
    schedule_date: string | null;
}

export interface StoreAd {
    id: number;
    title: string;
    description: string | null;
    image_url: string;
    service_types_id: number;
    offers_id: number | null;
    promotions_id: number | null;
    coupons_id: number | null;
    events_id: number | null;
    button_text: string | null;
    conditions_text: string | null;
    start_date: string;
    end_date: string;
    status: 'active' | 'scheduled' | 'finished';
    budget: number | null;
    spent: number;
    audience: AudienceSegment | null;
    impressions: number;
    clicks: number;
    channels: AdChannel[];
}

// Edición de una campaña en curso (canal, contenido y audiencia no cambian)
export interface AdUpdatePayload {
    title: string;
    description: string;
    button_text: string;
    conditions_text: string;
    end_date: string;
    add_budget: number;                 // se cobra del saldo
    push_schedule_date: string | null;  // solo si el push no se ha enviado
}

export interface AdPayload {
    service_types_id: number;
    content_id: number | null;
    title: string;
    description: string;
    button_text: string;
    conditions_text: string;
    start_date: string;
    end_date: string;
    display: { service_id: number | null; budget: number };
    push: { enabled: boolean; audience: AudienceSegment; schedule_date: string | null };
}
